// Componente de presentación que gestiona la selección y resumen de productos del pedido.
import { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Product } from '@shared/components/ui/Product';
import { StepperInput } from '@shared/components/ui/StepperInput';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCircleInfo, faTrash } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';
import { ProductSelectorModal, ProductSelectorModalRef } from '../modals';
import type { ProductItem } from '../../types';
import { formatCurrencyAmount, useCurrency, useCurrencySymbol } from '../../../shared/hooks/useCurrency';

interface ProductsProps {
  isLocked?: boolean;
  tenantChildId?: number;
}

export interface ProductsRef {
  getProductsCount: () => number;
  getProducts: () => ProductItem[];
  setProducts: (products: ProductItem[]) => void;
  getShippingCosts: () => number;
  getDiscounts: () => number;
}

export const Products = forwardRef<ProductsRef, ProductsProps>(({ isLocked = false, tenantChildId }, ref) => {
  const { t } = useTranslation('createEditOrdersModule');
  const productSelectorModalRef = useRef<ProductSelectorModalRef>(null);
  const confirmDeleteModalRef = useRef<ConfirmModalRef>(null);

  const [products, setProducts] = useState<ProductItem[]>([]);
  /** Texto del importe por producto: permite borrar el campo sin forzar "0" en pantalla (mismo patrón que SelectedItems). */
  const [priceInputStrings, setPriceInputStrings] = useState<Record<number, string>>({});
  const [shippingCosts, setShippingCosts] = useState<number>(0);
  const [discounts, setDiscounts] = useState<number>(0);
  const isDisabled = isLocked;

  const handleAddProduct = () => {
    if (isDisabled) {
      return;
    }
    productSelectorModalRef.current?.openModal();
  };

  const handleUpdateProductsFromModal = (
    updatedProducts: ProductItem[],
    newShippingCosts?: number,
    newDiscounts?: number,
  ) => {
    setProducts(updatedProducts);
    setPriceInputStrings({});
    if (newShippingCosts !== undefined) {
      setShippingCosts(newShippingCosts);
    }
    if (newDiscounts !== undefined) {
      setDiscounts(newDiscounts);
    }
  };

  const handleQuantityChange = (productId: number, newQuantity: number) => {
    setProducts((prev) =>
      prev.map((product) => (product.id === productId ? { ...product, quantity: newQuantity } : product)),
    );
  };

  const handlePriceInputChange = (productId: number, raw: string) => {
    setPriceInputStrings((prev) => ({ ...prev, [productId]: raw }));

    const trimmed = raw.trim();
    const numeric = trimmed === '' ? 0 : parseFloat(trimmed);

    setProducts((prev) =>
      prev.map((product) =>
        product.id === productId ? { ...product, price: Number.isNaN(numeric) ? 0 : numeric } : product,
      ),
    );
  };

  /** Al salir del campo, volvemos al valor numérico canónico (evita restos tipo "049.99" en pantalla). */
  const handlePriceInputBlur = (productId: number) => {
    setPriceInputStrings((prev) => {
      if (!(productId in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const handleRemoveProduct = (productId: number) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      confirmDeleteModalRef.current?.open(
        () => {
          setProducts((prev) => prev.filter((p) => p.id !== productId));
          setPriceInputStrings((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
          });
        },
        undefined,
        t('products.confirmDelete.title'),
        t('products.confirmDelete.message', { productName: product.name }),
        t('products.confirmDelete.confirm'),
        t('products.confirmDelete.cancel'),
      );
    }
  };

  // Cálculos del resumen
  const subtotal = products.reduce((sum, product) => sum + product.price * product.quantity, 0);

  // Gastos de envío: solo el valor editado por el usuario (sin cálculo automático hasta tener dirección)
  const finalShippingCost = shippingCosts;
  const total = subtotal + finalShippingCost - discounts;

  // Obtener el símbolo de moneda del usuario
  const currency = useCurrency();
  const currencySymbol = useCurrencySymbol();

  // Exponer el conteo de productos al componente padre
  useImperativeHandle(ref, () => ({
    getProductsCount: () => products.length,
    getProducts: () => products,
    setProducts: (newProducts: ProductItem[]) => {
      setProducts(newProducts);
      setPriceInputStrings({});
    },
    getShippingCosts: () => finalShippingCost,
    getDiscounts: () => discounts,
  }));

  return (
    <>
      <div className="p-4 space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-5 pt-4 border-b border-card-border">
        <div className="col-span-1 md:col-span-2">
          <h2 className="text-lg font-bold">{t('products.title')}</h2>
        </div>
        <div className="col-span-1 md:col-span-3 !mt-0">
          <div className="space-y-4">
            {/* Botón para agregar productos */}
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="default"
                onClick={handleAddProduct}
                className="flex items-center gap-2"
                disabled={isDisabled}
                data-testid="add-products-button"
              >
                <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                {t('products.addProducts')}
              </Button>
            </div>

            {/* Alert cuando no hay productos */}
            {products.length === 0 && (
              <Alert className="border-card-border bg-card-bg">
                <FontAwesomeIcon icon={faCircleInfo} className="w-4 h-4" />
                <AlertDescription>{t('products.noProductsMessage')}</AlertDescription>
              </Alert>
            )}

            {/* Lista de productos y resumen */}
            {products.length > 0 && (
              <div className="space-y-6">
                {/* Encabezados de la tabla - Solo visible en desktop */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 text-xs font-medium text-complementary-text border-b border-card-border pb-2">
                  <div className="col-span-6">{t('products.table.product')}</div>
                  <div className="col-span-3">{t('products.table.amount')}</div>
                  <div className="col-span-2">{t('products.table.quantity')}</div>
                  <div className="col-span-1"></div>
                </div>

                {/* Lista de productos */}
                <div className="space-y-4">
                  {products.map((product) => (
                    <div key={product.id} className="border-b border-card-border pb-4">
                      {/* Desktop Layout */}
                      <div className="hidden md:grid md:grid-cols-12 gap-4 items-center py-2">
                        {/* Información del producto */}
                        <div className="col-span-6">
                          <Product
                            id={product.id}
                            name={product.name}
                            src={product.image}
                            code={product.code}
                            showImageModal={true}
                          />
                        </div>

                        {/* Campo de importe */}
                        <div className="col-span-3">
                          <Input
                            type="number"
                            inputMode="decimal"
                            value={priceInputStrings[product.id] ?? String(product.price)}
                            onChange={(e) => handlePriceInputChange(product.id, e.target.value)}
                            onBlur={() => handlePriceInputBlur(product.id)}
                            className="w-full text-left"
                            min="0"
                            step="0.01"
                            maxLength={250}
                            disabled={isDisabled}
                            data-testid={`create-order-product-price-input-${product.id}`}
                          />
                        </div>

                        {/* Stepper de cantidad */}
                        <div className="col-span-2">
                          <StepperInput
                            value={product.quantity}
                            onChange={(value) => handleQuantityChange(product.id, value)}
                            max={999}
                            className="w-full"
                            disabled={isDisabled}
                          />
                        </div>

                        {/* Botón de eliminar */}
                        <div className="col-span-1 flex justify-end">
                          <button
                            onClick={() => handleRemoveProduct(product.id)}
                            className="p-2 disabled:opacity-50"
                            title={t('products.actions.removeProduct')}
                            disabled={isDisabled}
                          >
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Mobile Layout */}
                      <div className="md:hidden space-y-3">
                        {/* Fila 1: Información del producto */}
                        <div className="flex items-start gap-3">
                          <Product
                            id={product.id}
                            name={product.name}
                            src={product.image}
                            code={product.code}
                            showImageModal={true}
                          />
                        </div>

                        {/* Fila 2: Importe, Stepper y Botón eliminar */}
                        <div className="flex items-center gap-3">
                          {/* Campo de importe */}
                          <div className="flex-1">
                            <Input
                              type="number"
                              inputMode="decimal"
                              value={priceInputStrings[product.id] ?? String(product.price)}
                              onChange={(e) => handlePriceInputChange(product.id, e.target.value)}
                              onBlur={() => handlePriceInputBlur(product.id)}
                              className="w-full text-center"
                              min="0"
                              step="0.01"
                              maxLength={250}
                              disabled={isDisabled}
                              data-testid={`create-order-product-price-input-mobile-${product.id}`}
                            />
                          </div>

                          {/* Stepper de cantidad */}
                          <div className="flex-1">
                            <StepperInput
                              value={product.quantity}
                              onChange={(value) => handleQuantityChange(product.id, value)}
                              max={999}
                              className="w-full"
                              disabled={isDisabled}
                            />
                          </div>

                          {/* Botón de eliminar */}
                          <button
                            onClick={() => handleRemoveProduct(product.id)}
                            className="p-2 disabled:opacity-50"
                            title={t('products.actions.removeProduct')}
                            disabled={isDisabled}
                          >
                            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Resumen financiero */}
                <div className="space-y-3 pt-2 border-t border-card-border">
                  {/* Subtotal Row - Fila de subtotal (suma de todos los productos) */}
                  <div className="flex justify-between items-center text-sm pb-2 pt-3">
                    <span className="text-complementary-text">{t('products.summary.subtotal')}</span>
                    <span>{formatCurrencyAmount(subtotal, currency)}</span>
                  </div>

                  {/* Shipping Costs Row - Fila de costos de envío (editable por el usuario) */}
                  <div className="flex justify-between items-center text-sm gap-2">
                    <span className="text-complementary-text">{t('products.summary.shippingCosts')}</span>
                    {/* Shipping Cost Input - Campo de entrada para costos de envío */}
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={finalShippingCost}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setShippingCosts(value);
                        }}
                        className="w-24 text-right"
                        min="0"
                        step="0.01"
                        data-testid="shipping-costs-input"
                        disabled={isDisabled}
                      />
                      <span className="text-complementary-text">{currencySymbol}</span>
                    </div>
                  </div>

                  {/* Discounts Row - Fila de descuentos (editable por el usuario) */}
                  <div className="flex justify-between items-center text-sm gap-2">
                    <span className="text-complementary-text">{t('products.summary.discounts')}</span>
                    {/* Discount Input - Campo de entrada para descuentos */}
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={discounts}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          setDiscounts(value);
                        }}
                        className="w-24 text-right"
                        min="0"
                        step="0.01"
                        data-testid="discounts-input"
                        disabled={isDisabled}
                      />
                      <span className="text-complementary-text">{currencySymbol}</span>
                    </div>
                  </div>

                  {/* Total Row - Fila de total (cálculo final: subtotal + envío - descuentos) */}
                  <div className="flex justify-between items-center text-lg font-bold pt-2 border-t border-card-border">
                    <span>{t('products.summary.total')}</span>
                    <span>{formatCurrencyAmount(total, currency)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de selección de productos */}
      <ProductSelectorModal
        ref={productSelectorModalRef}
        onUpdateProducts={handleUpdateProductsFromModal}
        currentOrderProducts={products}
        currentShippingCosts={shippingCosts}
        currentDiscounts={discounts}
        tenantChildId={tenantChildId}
      />

      {/* Modal de confirmación para eliminar producto */}
      <ConfirmModal ref={confirmDeleteModalRef} />
    </>
  );
});
